import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { Typography, Spin } from "antd";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import eventBus from "src/common/eventBus";
import { Button } from "src/components";
import api from "src/config/api";
import { formatDate } from "src/utils/date";
import "./ContractStyle.css";
import ContractResponsibilities from "./ContractResponsibilities";

const ContractDetail = () => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const shouldDownload = searchParams.get('download') === 'true';
  const navigate = useNavigate();
  const [contract, setContract] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [pdfDownloaded, setPdfDownloaded] = useState(false);
  const contractRef = useRef(null);

  useEffect(() => {
    const fetchContract = async () => {
      try {
        const response = await api.get(`contracts/${id}`);
        setContract(response.data);
      } catch (error) {
        console.error("Error fetching contract:", error);
        eventBus.emit("dialog", {
          type: "error",
          message: "Failed to load contract",
        });
      } finally {
        setLoading(false);
      }
    };

    fetchContract();
  }, [id]);

  // Auto-download PDF if the URL has ?download=true parameter
  useEffect(() => {
    if (!loading && contract && shouldDownload && contractRef.current && !pdfDownloaded) {
      setPdfDownloaded(true);
      generatePdf();
    }
  }, [loading, contract, shouldDownload, pdfDownloaded]);

  const generatePdf = async () => {
    try {
      setDownloadingPdf(true);
      
      const contractElement = contractRef.current;
      if (!contractElement) return;
      
      const canvas = await html2canvas(contractElement, {
        scale: 2, // Higher scale for better quality
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff'
      });
      
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      
      const imgWidth = canvas.width;
      const imgHeight = canvas.height;
      const ratio = Math.min(pdfWidth / imgWidth, pdfHeight / imgHeight);
      const imgX = (pdfWidth - imgWidth * ratio) / 2;
      const imgY = 10;
      
      pdf.addImage(imgData, 'PNG', imgX, imgY, imgWidth * ratio, imgHeight * ratio);
      
      // Create a consistent filename with customer name if available
      const customerName = contract.customerName ? 
        contract.customerName.replace(/\s+/g, '_') : 'Customer';
      const dateStr = new Date().toISOString().split('T')[0];
      
      pdf.save(`JustBikes_Contract_${customerName}_${dateStr}.pdf`);
      
      // If this was triggered by the download parameter, close the window after download
      if (shouldDownload) {
          window.close();
      }
    } catch (error) {
      console.error('Error generating PDF:', error);
      eventBus.emit("dialog", {
        type: "error",
        message: "Failed to generate PDF",
      });
    } finally {
      setDownloadingPdf(false);
    }
  };

  if (loading) {
    return <Spin tip="Loading contract..." />;
  }

  if (!contract) {
    return <Typography.Title level={4}>Contract not found</Typography.Title>;
  }

  return (
    <>
      <div className="page-header">
        <Typography.Title level={4}>Contract Details</Typography.Title>
        <div>
          <Button onClick={() => navigate("/contracts")} style={{ marginRight: 10 }}>
            Back to List
          </Button>
          <Button 
            type="primary" 
            onClick={generatePdf}
            loading={downloadingPdf}
          >
            Download PDF
          </Button>
        </div>
      </div>
      
      <div className="contract-container view-mode" ref={contractRef}>
        <div className="contract-header">
          <div className="logo">Just Bikes</div>
          <div className="subtitle">Motorbike, bicycle rental & tours</div>
          <div className="contact">Phone/WhatsApp: +84 77 7507 323</div>
          <div className="hours">OPEN 9AM TO 5PM</div>
        </div>

        <h2 className="contract-title">Rental Contract</h2>

        <div className="contract-form">
          <div className="form-row">
            <div className="form-label">Name:</div>
            <div className="form-value">{contract.customerName || '-'}</div>
          </div>

          <div className="form-row">
            <div className="form-label">Nationality:</div>
            <div className="form-value">{contract.nationality || '-'}</div>
          </div>

          <div className="form-row">
            <div className="form-label">Phone/WhatsApp:</div>
            <div className="form-value">{contract.phoneWhatsapp || '-'}</div>
          </div>

          <div className="form-row">
            <div className="form-label">Address/Hotel:</div>
            <div className="form-value">{contract.addressHotel || '-'}</div>
          </div>

          <div className="form-row">
            <div className="form-label">Bike:</div>
            <div className="form-value">{contract.bikeName || '-'}</div>
          </div>

          <div className="form-row">
            <div className="form-label">Number:</div>
            <div className="form-value">{contract.licenseNumber || '-'}</div>
          </div>

          <div className="form-row">
            <div className="form-label">Start date and time:</div>
            <div className="form-value">{formatDate(contract.startDate) || '-'}</div>
          </div>

          <div className="form-row">
            <div className="form-label">End date and time:</div>
            <div className="form-value">{formatDate(contract.endDate) || '-'}</div>
          </div>

          <div className="form-row">
            <div className="form-label">Price:</div>
            <div className="form-value">{contract.price || '-'}</div>
          </div>

          <div className="form-row">
            <div className="form-label">Deposit:</div>
            <div className="form-value">{contract.deposit || '-'}</div>
          </div>

          <div className="form-row">
            <div className="form-label">Deposit Type:</div>
            <div className="form-value">
              {[
                contract.depositPassport && 'Passport',
                contract.depositIDCard && 'ID Card',
                contract.depositDrivingLicense && 'Driving License',
                contract.depositCash && 'Cash'
              ].filter(Boolean).join(', ') || '-'}
            </div>
          </div>

          <div className="form-row">
            <div className="form-label">Helmets:</div>
            <div className="helmet-inputs">
              {contract.helmetLarge > 0 && (
                <div className="helmet-input">
                  <span className="helmet-label">Large:</span>
                  <span>{contract.helmetLarge}</span>
                </div>
              )}
              {contract.helmetSmall > 0 && (
                <div className="helmet-input">
                  <span className="helmet-label">Small:</span>
                  <span>{contract.helmetSmall}</span>
                </div>
              )}
              {!contract.helmetLarge && !contract.helmetSmall && (
                <span>None</span>
              )}
            </div>
          </div>
          <div className="form-row">
            <div className="form-label">Extras:</div>
            <div className="extras-options">
              <div className="form-value" style={{ width: '100%' }}>
                {[
                  contract.phoneHolder && 'Phone Holder',
                  contract.bicycleLock && 'Bicycle Lock',
                  contract.bicycleLights && 'Bicycle Lights'
                ].filter(Boolean).join(', ') || '-'}
              </div>
            </div>
          </div>

          <ContractResponsibilities />

          <div className="signature-section">
            <div className="signature-container">
              <div className="signature-label">Signature:</div>
              {contract.signature ? (
                <div className="signature-image">
                  <img src={contract.signature} alt="Customer Signature" style={{ maxWidth: '300px', maxHeight: '100px' }} />
                </div>
              ) : (
                <div className="no-signature">No signature</div>
              )}
            </div>
            
            <div className="date-container">
              <div className="date-label">Date:</div>
              <div className="form-value">
                {formatDate(contract.createDate)}
              </div>
            </div>
          </div>
          
          {/* Return section - only show if return signature exists */}
          {contract.returnSignature && (
            <div className="return-section">
              <h3>Bike Return</h3>
              
              {contract.returnStatus && (
                <div className="form-row">
                  <div className="form-label">Return Status:</div>
                  <div className="form-value">
                    {contract.returnStatus === 'good' && 'Good condition'}
                    {contract.returnStatus === 'minor_damages' && 'Minor damages'}
                    {contract.returnStatus === 'major_damages' && 'Major damages'}
                  </div>
                </div>
              )}
              
              {contract.additionalCharges > 0 && (
                <div className="form-row">
                  <div className="form-label">Additional Charges:</div>
                  <div className="form-value">{contract.additionalCharges}</div>
                </div>
              )}
              
              {contract.returnNotes && (
                <div className="form-row">
                  <div className="form-label">Notes:</div>
                  <div className="form-value">{contract.returnNotes}</div>
                </div>
              )}
              
              <div className="signature-section">
                <div className="signature-container">
                  <div className="signature-label">Return Signature:</div>
                  <div className="signature-image">
                    <img src={contract.returnSignature} alt="Return Signature" style={{ maxWidth: '300px', maxHeight: '100px' }} />
                  </div>
                </div>
                
                <div className="date-container">
                  <div className="date-label">Return Date:</div>
                  <div className="form-value">
                    {formatDate(contract.returnDate)}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default ContractDetail; 